import { useRef, useState } from 'react';
import { ActLink } from '../ui/ActLink';
import { AddButton } from '../ui/AddButton';
import { useAiScope } from '../assistant/AiButton';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { can, type SessionUser } from '@dpw/shared';
import { api } from '../api';
import { useChange, useSpace, useTrack, useTrackActivities, useTrackFiles, useTrackOverview, useTrackUpdates } from '../lib/queries';
import { LayoutGroup, motion } from 'motion/react';
import type { Activity, TrackDetail, TrackOverview, TrackUpdate } from '@dpw/shared';
import { BriefPanel } from '../ui/BriefPanel';
import { Glide, Skel, SkelList } from '../ui/motion';
import { useHotkeys } from '../lib/keys';
import { listItem, spring } from '../lib/motion';
import { agoStart, dayLabel, localDate, localDay } from '../lib/format';
import { ActivityTable } from '../ui/ActivityTable';
import { Avatar, Diamond, ErrorNote, HEALTH, Loading, StatusPill } from '../ui/bits';
import { NewActivity } from '../ui/NewActivity';
import { QuickAdd } from '../ui/QuickAdd';
import { FileList } from '../ui/FileList';
import { ContextPanel, ParentContext } from '../ui/ContextPanel';
import { useToast } from '../ui/toast';
import { Strip } from '../ui/Strip';
import { usePhone } from '../lib/media';
import { PhoneTrack } from '../phone/Track';
import { useInboundAddress } from '../ui/email';
import { ArchiveButton } from '../ui/Archive';
import { useAssistantPanel } from '../assistant/Panel';

type Tab = 'activities' | 'context' | 'thread' | 'minutes' | 'emails' | 'files';
const TABS: readonly Tab[] = ['activities', 'context', 'thread', 'minutes', 'emails', 'files'];
const TAB_LABEL: Record<Tab, string> = { activities: 'Activities', context: 'Context', thread: 'Thread', minutes: 'Minutes', emails: 'Emails', files: 'Files' };

export function TrackPage({ me }: { me: SessionUser }) {
  const panel = useAssistantPanel();
  const { id = '' } = useParams();
  const track = useTrack(id);
  const space = useSpace(track.data?.spaceId ?? '');
  useAiScope(track.data ? { spaceId: track.data.spaceId, trackId: track.data.id, label: track.data.name } : null);
  const [params, setParams] = useSearchParams();
  const tab = TABS.find(t => t === params.get('tab')) ?? 'activities';
  const setTab = (t: Tab) => setParams(t === 'activities' ? {} : { tab: t }, { replace: true });
  const thread = useTrackActivities(id, true);
  const minutes = useTrackUpdates(id, 'minutes');
  const emails = useTrackUpdates(id, 'email');
  const overview = useTrackOverview(id);
  useHotkeys({ n: () => { if (can(me.role, 'activity.write') && space.data) setSheet(true); } });
  const [done, setDone] = useState(false);
  const acts = useTrackActivities(id, done);
  const files = useTrackFiles(id);
  const [sheet, setSheet] = useState(false);
  const [filter, setFilter] = useState<'all' | 'track' | 'activities'>('all');
  const fileInput = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const phone = usePhone();
  const upload = useChange((f: File) => api.tracks.upload(id, f, f.name));

  if (track.isLoading) return <div className="col gap12" role="status" aria-label="Loading"><Skel w="40%" h={28} r={8} /><Skel h={52} r={14} /><Skel h={80} r={16} /><SkelList rows={5} variant="conv" /></div>;
  if (track.error || !track.data) return <ErrorNote error={track.error} />;
  const t = track.data;
  if (phone) return <PhoneTrack t={t} me={me} />;
  const shown = (files.data ?? []).filter(f => filter === 'all' || (filter === 'track' ? !f.activityId : !!f.activityId));

  return (
    <>
      <div className="hdr">
        <div className="htl">
          <Link className="xs mut" to={`/spaces/${t.spaceId}`}>{t.spaceName} /</Link>
          <h1>{t.name}</h1>
          <span className="xs mut">{t.lead ? `Lead ${t.lead.name} · ` : ''}{t.total} activities</span>
        </div>
        <div className="hact">
          <div className="stack">{t.people.slice(0, 5).map(p => <Avatar key={p.id} p={p} />)}</div>
          <button type="button" className="bt" onClick={() => panel.open({ trackId: t.id, spaceId: t.spaceId, label: t.name, prompt: `What are the updates on ${t.name}?`, send: true })}>Ask about this track</button>
          {can(me.role, 'activity.write') && <AddButton onClick={() => setSheet(true)} disabled={!space.data}>New activity</AddButton>}
          {can(me.role, 'track.write') && <ArchiveButton kind="track" id={t.id} name={t.name} count={t.total} then={`/spaces/${t.spaceId}`} />}
        </div>
      </div>
      <div className="metas">
        <div><b>Status</b><span style={{ color: `var(--${HEALTH[t.health][1]})` }} title={t.healthWhy ? `${t.healthWhy}. Worked out from the activities.` : 'Nothing late or at risk. Worked out from the activities.'}>{HEALTH[t.health][0]}</span></div>
        <div><b>Next</b><span>{t.next ? <ActLink id={t.next.id}>{t.next.title} · {dayLabel(t.next.date)}</ActLink> : 'Nothing scheduled'}</span></div>
        <div><b>Open</b><span>{t.open} activities</span></div>
        <div><b>Overdue</b><span style={{ color: t.overdue ? 'var(--red-tx)' : undefined }}>{t.overdue}</span></div>
        <div><b>Last movement</b><span>{t.lastMovement ? agoStart(t.lastMovement) : 'None yet'}</span></div>
        <div><b>Tags</b><span className="mono">{t.tags.map(x => `#${x}`).join(' ') || 'None'}</span></div>
      </div>
      <BriefPanel kind="track" id={t.id} />
      <LayoutGroup id="track-tabs">
        <Strip className="tabs3" role="tablist" aria-label="Track">
          {TABS.map(k => {
            const n = k === 'activities' ? t.open : k === 'thread' ? thread.data?.length ?? 0 : k === 'minutes' ? minutes.data?.length ?? 0 : k === 'emails' ? emails.data?.length ?? 0 : k === 'files' ? files.data?.length ?? 0 : 0;
            return (
              <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>
                {TAB_LABEL[k]}{n ? <span className="ct">{n}</span> : null}
                {tab === k && <motion.span layoutId="track-tab" className="tabmark" transition={spring.snappy} />}
              </button>
            );
          })}
        </Strip>
      </LayoutGroup>
      <div className="trk">
        <div className="trkmain">
          {tab === 'context' ? (
            <div className="col gap12">
              <ParentContext spaceId={t.spaceId} />
              <ContextPanel kind="track" id={t.id} spaceOf={t.spaceId} />
            </div>
          ) : tab === 'activities' ? (
            <Glide className="col gap12">
              {(overview.data?.keyDates.length ?? 0) > 0 && (
                <div className="keyd" aria-label="Key dates">
                  <span className="k">Key dates</span>
                  {overview.data!.keyDates.map(k => <ActLink key={k.id} id={k.id} className={k.done ? 'done' : ''}><Diamond sm />{k.title} <b>{dayLabel(k.date)}</b></ActLink>)}
                </div>
              )}

        <div className="tblwrap">
          {can(me.role, 'activity.write') && space.data && <QuickAdd key={t.id} space={space.data} me={me} trackId={t.id} />}
          <div className="tblhead" style={{ minHeight: 44, paddingTop: 0 }}>
            <span className="xs mut" style={{ flex: 1 }}>{acts.data?.length ?? 0} shown</span>
            <div className="seg2"><button className={done ? 'on' : ''} onClick={() => setDone(!done)} aria-pressed={done}>Show done</button></div>
          </div>
          {acts.isLoading ? <Loading /> : <ActivityTable rows={acts.data ?? []} showTrack={false} />}
        </div>
            </Glide>
          ) : tab === 'thread' ? <Thread rows={thread.data} />
          : tab === 'minutes' ? <UpdatesList rows={minutes.data} kind="minutes" />
          : tab === 'emails' ? <UpdatesList rows={emails.data} kind="emails" />
          : (

        <div className="panel fpan">
          <div className="fbar">
            <div className="fchips">
              {(['all', 'track', 'activities'] as const).map(k => (
                <button key={k} className={`fc${filter === k ? ' on' : ''}`} onClick={() => setFilter(k)}>
                  {k === 'all' ? 'All' : k === 'track' ? 'On the track' : 'On activities'}
                  <span>{(files.data ?? []).filter(f => k === 'all' || (k === 'track' ? !f.activityId : !!f.activityId)).length}</span>
                </button>
              ))}
            </div>
            {can(me.role, 'file.write') && <AddButton onClick={() => fileInput.current?.click()}>Add file</AddButton>}
            <input ref={fileInput} type="file" hidden onChange={e => { const f = e.target.files?.[0]; if (f) upload.mutate(f, { onSuccess: () => toast(`Added ${f.name}`) }); e.target.value = ''; }} />
          </div>
          {upload.isPending && <div className="rowc xs mut"><span className="spin" />Uploading</div>}
          {upload.error ? <ErrorNote error={upload.error} /> : null}
          <FileList files={shown} showWhere />
        </div>
          )}
        </div>
        <TrackSide t={t} o={overview.data} loading={overview.isLoading} />
      </div>
      {sheet && space.data && <NewActivity space={space.data} me={me} trackId={t.id} onClose={() => setSheet(false)} />}
    </>
  );
}

const dateOf = (a: Activity) => a.startsAt ? localDay(a.startsAt) : a.dueDate ?? localDay(a.createdAt);

/** The track's story in time order: follow-ups under the meeting they came from, a NOW line between past and future. */
function Thread({ rows }: { rows: Activity[] | undefined }) {
  if (!rows) return <SkelList rows={6} variant="conv" />;
  if (!rows.length) return <div className="panel empty0"><b>Nothing on this track yet.</b><span className="xs mut">Activities appear here in time order, with what each one led to.</span></div>;
  const byId = new Map(rows.map(a => [a.id, a]));
  // Under each activity: its parts and its follow-ups (what it led to), in time order.
  const up = (a: Activity) => a.parentActivityId ?? a.originActivityId;
  const roots = rows.filter(a => !up(a) || !byId.has(up(a)!)).sort((x, y) => dateOf(x).localeCompare(dateOf(y)));
  const kids = (id: string) => rows.filter(a => up(a) === id).sort((x, y) => dateOf(x).localeCompare(dateOf(y)));
  const flat: { a: Activity; depth: number; from: Activity | null }[] = [];
  const walk = (a: Activity, depth: number) => { flat.push({ a, depth, from: depth ? byId.get(up(a)!) ?? null : null }); for (const k of kids(a.id)) walk(k, depth + 1); };
  roots.forEach(r => walk(r, 0));
  const today = localDate();
  const nowAt = flat.findIndex(x => x.depth === 0 && dateOf(x.a) >= today);
  return (
    <div className="panel thr2">
      {flat.map((x, i) => (
        <div key={x.a.id}>
          {i === nowAt && <div className="nowl" aria-label="Now"><span>Now · {dayLabel(today)}</span></div>}
          <motion.div className={`tli d${Math.min(x.depth, 2)}${x.a.status === 'done' ? ' past' : ''}`} variants={listItem} initial="initial" animate="animate">
            <span className={`tdot ${x.a.status === 'done' ? 'done' : x.a.isOverdue || x.a.status === 'at_risk' || x.a.status === 'blocked' ? 'red' : 'open'}`} aria-hidden="true" />
            <div className="tbody">
              <div className="rowc gap6"><ActLink id={x.a.id} className="tt">{x.a.isMilestone && <Diamond sm />}{x.a.title}</ActLink>{x.from && <span className="tfrom">from {x.from.title}</span>}</div>
              <div className="xs mut">{x.depth ? 'Follow-up' : TYPE_WORD[x.a.type]} · {dayLabel(dateOf(x.a))}{x.a.owners[0] ? ` · ${x.a.owners[0].name.split(' ')[0]}` : ''}</div>
            </div>
            <StatusPill status={x.a.status} overdue={x.a.isOverdue} />
          </motion.div>
        </div>
      ))}
      {nowAt === -1 && <div className="nowl" aria-label="Now"><span>Now · {dayLabel(today)}</span></div>}
    </div>
  );
}
const TYPE_WORD = { meeting: 'Meeting', workshop: 'Workshop', deliverable: 'Deliverable', task: 'Task' } as const;

/** Minutes: each filed minutes, how it was recorded, Open. Emails: each logged email, who sent it, when. */
function UpdatesList({ rows, kind }: { rows: TrackUpdate[] | undefined; kind: 'minutes' | 'emails' }) {
  const addr = useInboundAddress();
  if (!rows) return <SkelList rows={3} variant="conv" />;
  if (!rows.length) return <div className="panel empty0"><b>{kind === 'minutes' ? 'No minutes yet.' : 'No emails logged yet.'}</b><span className="xs mut">{kind === 'minutes' ? 'Minutes filed on any activity of this track show here.' : <>Forward an email{addr ? <> to <span className="mono inaddr">{addr}</span></> : null}, or copy it in, and approve where it goes; it shows here.</>}</span></div>;
  const how = (u: TrackUpdate) => u.source === 'recording' ? 'recorded in app' : u.source === 'assistant' ? 'filed by the assistant' : 'typed';
  return (
    <div className="panel ulist">
      {rows.map(u => (
        <div className="lrow" key={u.id}>
          <span className="ubadge" aria-hidden="true">{kind === 'minutes' ? 'MIN' : 'EML'}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sm" style={{ fontWeight: 500 }}>{kind === 'minutes' ? u.activity.title : u.body.split('\n')[0]}</div>
            <div className="xs mut">{kind === 'minutes' ? `${shortWhen(u.createdAt)} · ${how(u)}` : `${u.author?.name ?? 'Forwarded'} · logged ${shortWhen(u.createdAt)} · ${u.activity.title}`}</div>
          </div>
          <Link className="bt sm" to={`/activities/${u.activity.id}${kind === 'minutes' ? '?tab=minutes' : '?tab=updates'}`}>Open</Link>
        </div>
      ))}
    </div>
  );
}
const shortWhen = (iso: string) => dayLabel(localDay(iso));

/** Signals, meeting chains and people: what needs attention on this track. */
function TrackSide({ t, o, loading }: { t: TrackDetail; o: TrackOverview | undefined; loading: boolean }) {
  return (
    <Glide as="aside" className="trkside" aria-label="About this track">
      <div className="panel">
        <div className="sh"><b>Signals</b></div>
        {loading ? <SkelList rows={3} variant="conv" /> : !o?.signals.length ? <div className="xs mut">Nothing needs attention.</div> : o.signals.map((x, i) => (
          <Link key={i} className="sig2" to={x.activityId ? `/activities/${x.activityId}` : '#'}>
            <span className={`dt ${x.tone === 'ai' ? 'acc' : x.tone}`} /><span><span className="sm">{x.title}</span><span className="xs mut">{x.detail}</span></span>
          </Link>
        ))}
      </div>
      <div className="panel">
        <div className="sh"><b>Meeting chains</b><span className="xs mut">A meeting and what it led to</span></div>
        {loading ? <SkelList rows={2} variant="conv" /> : !o?.chains.length ? <div className="xs mut">No meeting has led to follow-ups yet.</div> : o.chains.map(c => (
          <Link key={c.id} className="chain" to={`/activities/${c.id}`}>
            <span className="tfrom">{c.title.split(/[:,]/)[0]}</span>
            <span className="sm">{c.followUps ? `${c.followUps} follow-up${c.followUps === 1 ? '' : 's'}, ${c.followUps - c.done ? `${c.followUps - c.done} open` : `${c.done} done`}` : `${c.date ? dayLabel(c.date) : 'Not scheduled'}, ${c.prepOpen} prep item${c.prepOpen === 1 ? '' : 's'}`}</span>
          </Link>
        ))}
      </div>
      <div className="panel">
        <div className="sh"><b>People</b></div>
        {t.people.length ? [...t.people].sort((x, y) => (x.id === t.lead?.id ? -1 : y.id === t.lead?.id ? 1 : y.open - x.open)).map(p => (
          <div className="lrow" key={p.id}><Avatar p={p} /><span className="sm" style={{ flex: 1 }}>{p.name.split(' ')[0]}{t.lead?.id === p.id ? ' · lead' : ''}</span><span className="xs mut">{p.open} open</span></div>
        )) : <div className="xs mut">Nobody owns anything here yet.</div>}
      </div>
    </Glide>
  );
}
